import {useState, useEffect, useRef} from 'react';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {colors} from 'shells/colors/colors';
import {
    NavIconEmailIcon,
    NavIconSettingsIcon,
    ShareIcon,
    StarIcon,
    SiteGlobeIcon,
    WarningIcon,
    CompanyContactIcon,
    NavIconSettingsWrenchIcon,
} from 'shells/icon';
import {SyncRegularIcon} from 'shells/icon/svg/sync-regular-icon';
import {Divider} from 'shells/layout/divider';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';

import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {AdBlockerDialog} from '../../../intercom/ad-blocker-dialog';
import {usePeepRedesignSurvey} from '../../../peep/onboarding/use-peep-redesign-survey';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';

import {ChatStatusButton} from './chat-status-button';
import {Logout} from './logout';
import {MeetYourCsm} from './meet-your-csm';
import {MenuItem} from './menu-item';
import {MenuItemSettingsToggle} from './menu-item-settings-toggle';

import styles from './user-menu-popover.module.css';

interface Props {
    user: User;
    setIsUserMenuOpened: (isUserMenuOpened: boolean) => void;
    setIsReferAFriendModalOpen: (isReferAFriendModalOpen: boolean) => void;
    setIsLeaveAReviewModalOpen: (isLeaveAReviewModalOpen: boolean) => void;
}

const lineDivider = <Divider variant='line--horizontal' />;

export const MenuItems = (props: Props) => {
    const [autoPresenceEnabled, setAutoPresenceEnabled] = useState<boolean>(
        props.user.autoPresenceEnabled
    );
    const [isBlockedDialogOpen, setIsBlockedDialogOpen] = useState<boolean>(false);
    const {openSurvey: openPeepSurvey, SurveyModal: PeepSurveyModal} = usePeepRedesignSurvey({
        isAdmin: Boolean(props.user.isAdministrator),
    });

    const menuRef = useRef<HTMLDivElement | null | undefined>(null);

    const isEmailSendingPaused = NutClientConfig.hasEmailSendingPaused;

    const closeUserMenu = () => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        props.setIsUserMenuOpened(false);
    };

    const handleOpenReferAFriendModal = () => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_PARTNERSTACK_REFERRAL_MODAL_OPENED);
        props.setIsReferAFriendModalOpen(true);
        closeUserMenu();
    };

    const handleOpenLeaveAReviewModal = () => {
        props.setIsLeaveAReviewModalOpen(true);
        closeUserMenu();
    };

    useEffect(() => {
        if (menuRef.current) {
            menuRef.current.focus({preventScroll: true});
        }
    }, []);

    return (
        // @ts-expect-error
        <div ref={menuRef} tabIndex={-1} className={styles['menu-items']}>
            {props.user.permissions.canUseInbox && (
                <>
                    <ChatStatusButton
                        user={props.user}
                        showDisabledUntil={
                            autoPresenceEnabled && props.user.agentStatus !== 'ONLINE'
                        }
                    />
                    {(autoPresenceEnabled || props.user.agentStatus === 'ONLINE') && (
                        <MenuItemSettingsToggle
                            user={props.user}
                            icon={SyncRegularIcon}
                            label='Automatically become available when using Nutshell'
                            propertyOnUser='autoPresenceEnabled'
                            settingName={Nut_Settings_User.SETTING_AUTO_PRESENCE_ENABLED}
                            reloadOnChange={false}
                            isSecondary={true}
                            activeBackgroundColor={colors.greenLt}
                            getState={setAutoPresenceEnabled}
                        />
                    )}
                    {lineDivider}
                </>
            )}
            {NutClientConfig.hasPeepPagesRedesignSwapperFF && (
                <>
                    <MenuItemSettingsToggle
                        user={props.user}
                        icon={CompanyContactIcon}
                        label='New person and company page layout'
                        propertyOnUser='peepPagesRedesignEnabled'
                        settingName={Nut_Settings_User.SETTING_PEEP_PAGES_REDESIGN_ENABLED}
                        reloadOnChange={true}
                        subText={
                            <Stack isHorizontal={true} spacing={4} alignItems='center'>
                                <Link variant='primary' size='small' onClick={openPeepSurvey}>
                                    Leave feedback
                                </Link>
                            </Stack>
                        }
                    />
                    {lineDivider}
                </>
            )}
            <button
                onClick={() => {
                    appHistory.push('/email/inbox');
                    closeUserMenu();
                }}
                tabIndex={0}
                className={styles['menu-item-button']}
            >
                <MenuItem
                    icon={NavIconEmailIcon}
                    text='Your inbox'
                    rightContent={
                        isEmailSendingPaused && (
                            <WarningIcon
                                // @ts-expect-error
                                color={colors.yellow}
                                size={16}
                                data-tip='Email sending is paused'
                                alignCenter={true}
                                wrapWithDiv={false}
                            />
                        )
                    }
                />
            </button>
            {lineDivider}
            <button
                onClick={() => {
                    appHistory.push('/my-account/settings');
                    closeUserMenu();
                }}
                tabIndex={0}
                className={styles['menu-item-button']}
            >
                <MenuItem icon={NavIconSettingsIcon} text='Your settings' />
            </button>
            {lineDivider}
            <button
                tabIndex={0}
                onClick={handleOpenReferAFriendModal}
                className={styles['menu-item-button']}
            >
                <MenuItem icon={ShareIcon} text='Refer a friend' />
            </button>
            {lineDivider}
            {NutClientConfig.canLeaveG2Review && (
                <>
                    <button
                        tabIndex={0}
                        onClick={handleOpenLeaveAReviewModal}
                        className={styles['menu-item-button']}
                    >
                        <MenuItem icon={StarIcon} text='Leave a review' />
                    </button>
                    {lineDivider}
                </>
            )}
            {((NutClientConfig.isDemo && !NutClientConfig.isTrial) ||
                NutClientConfig.isDevelopment) && (
                <>
                    <button
                        tabIndex={0}
                        onClick={() => {
                            window.open('/customer-site', '_blank');
                            closeUserMenu();
                        }}
                        className={styles['menu-item-button']}
                    >
                        <MenuItem icon={SiteGlobeIcon} text='Enter demo mode' />
                    </button>
                    {lineDivider}
                </>
            )}
            {NutClientConfig.isInternalLogin && (
                <>
                    <button
                        tabIndex={0}
                        onClick={() => {
                            window.open('/debug', '_blank');
                            closeUserMenu();
                        }}
                        className={styles['menu-item-button']}
                    >
                        <MenuItem icon={NavIconSettingsWrenchIcon} text='Debug Tools' />
                    </button>
                    {lineDivider}
                </>
            )}
            {NutClientConfig.shouldShowCsmInfo && <MeetYourCsm />}
            <Logout onClose={closeUserMenu} />
            {isBlockedDialogOpen && (
                <AdBlockerDialog onClose={() => setIsBlockedDialogOpen(false)} />
            )}
            {PeepSurveyModal}
        </div>
    );
};
